import React, { useState } from 'react';
import {
  Badge, Button, FormControl, FormLabel, HStack, IconButton, Input, Modal,
  ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, SimpleGrid, Switch, Table, TableContainer, Tbody, Td, Text,
  Textarea, Th, Thead, Tr, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../ConfirmModal';
import apiClient from '../../services/apiClient';
import ratePlanService from '../../services/ratePlanService';
import { apiError } from './format';

const EMPTY = { name: '', start_date: '', end_date: '', description: '', is_active: true };

const SeasonsPanel = ({ seasons, onChanged }) => {
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteRuleCount, setDeleteRuleCount] = useState(0);
  const modal = useDisclosure();
  const confirm = useDisclosure();
  const toast = useToast();
  const { t } = useTranslation();

  const open = (season) => {
    setEditing(season);
    setForm(season ? {
      name: season.name, start_date: season.start_date, end_date: season.end_date,
      description: season.description || '', is_active: season.is_active ?? true,
    } : EMPTY);
    modal.onOpen();
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await ratePlanService.updateSeason(editing.id, form);
      else await ratePlanService.createSeason(form);
      toast({ title: editing ? t('ratePlanSeasons.toasts.seasonSaved') : t('ratePlanSeasons.toasts.seasonAdded'), status: 'success', duration: 2000 });
      modal.onClose();
      onChanged();
    } catch (err) {
      toast({ title: t('ratePlanSeasons.toasts.saveFailed'), description: apiError(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  // Deleting a season deletes every rule priced on it (database
  // cascade), so count them first and say so in the confirmation.
  const askDelete = async (season) => {
    setDeleteTarget(season);
    try {
      const rules = await apiClient.get('/rates/rules/', { season: season.id });
      setDeleteRuleCount((Array.isArray(rules) ? rules : rules?.results || []).length);
    } catch {
      setDeleteRuleCount(0);
    }
    confirm.onOpen();
  };

  const remove = async () => {
    try {
      await ratePlanService.removeSeason(deleteTarget.id);
      toast({ title: t('ratePlanSeasons.toasts.seasonDeleted'), status: 'success', duration: 2000 });
      confirm.onClose();
      onChanged();
    } catch (err) {
      toast({ title: t('ratePlanSeasons.toasts.deleteFailed'), description: apiError(err), status: 'error' });
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.600" maxW="640px">
          {t('ratePlanSeasons.panel.intro')}
        </Text>
        <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue" onClick={() => open(null)}>
          {t('ratePlanSeasons.buttons.addSeason')}
        </Button>
      </HStack>
      <TableContainer borderWidth="1px" borderRadius="md" bg="white">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>{t('ratePlanSeasons.table.season')}</Th>
              <Th>{t('ratePlanSeasons.labels.from')}</Th>
              <Th>{t('ratePlanSeasons.table.to')}</Th>
              <Th>{t('ratePlanSeasons.table.status')}</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {seasons.length === 0 && (
              <Tr><Td colSpan={5}><Text fontSize="sm" color="gray.500" py={2}>{t('ratePlanSeasons.emptyState.noSeasons')}</Text></Td></Tr>
            )}
            {seasons.map((s) => {
              const current = s.start_date <= today && today <= s.end_date;
              return (
                <Tr key={s.id} opacity={s.is_active ? 1 : 0.5}>
                  <Td fontSize="sm" fontWeight="500">{s.name}</Td>
                  <Td fontSize="sm">{s.start_date}</Td>
                  <Td fontSize="sm">{s.end_date}</Td>
                  <Td>
                    {!s.is_active && <Badge fontSize="2xs">{t('ratePlanSeasons.badges.inactive')}</Badge>}
                    {s.is_active && current && <Badge colorScheme="green" fontSize="2xs">{t('ratePlanSeasons.badges.now')}</Badge>}
                    {s.is_active && s.end_date < today && <Badge fontSize="2xs">{t('ratePlanSeasons.badges.past')}</Badge>}
                  </Td>
                  <Td>
                    <HStack spacing={1} justify="flex-end">
                      <IconButton size="xs" variant="ghost" icon={<EditIcon />} aria-label={t('ratePlanSeasons.table.editSeason')} onClick={() => open(s)} />
                      <IconButton size="xs" variant="ghost" colorScheme="red" icon={<DeleteIcon />}
                        aria-label={t('ratePlanSeasons.table.deleteSeason')} onClick={() => askDelete(s)} />
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? t('ratePlanSeasons.modal.editTitle') : t('ratePlanSeasons.buttons.addSeason')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('ratePlanSeasons.labels.name')}</FormLabel>
                <Input size="sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t('ratePlanSeasons.labels.namePlaceholder')} />
              </FormControl>
              <SimpleGrid columns={2} spacing={4} w="100%">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('ratePlanSeasons.labels.from')}</FormLabel>
                  <Input size="sm" type="date" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('ratePlanSeasons.labels.toInclusive')}</FormLabel>
                  <Input size="sm" type="date" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm">{t('ratePlanSeasons.labels.notes')}</FormLabel>
                <Textarea size="sm" rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <Switch size="sm" mr={2} isChecked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <FormLabel fontSize="sm" mb={0}>{t('ratePlanSeasons.labels.active')}</FormLabel>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>{t('ratePlanSeasons.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={save} isLoading={saving}
              isDisabled={!form.name.trim() || !form.start_date || !form.end_date}>
              {editing ? t('ratePlanSeasons.buttons.save') : t('ratePlanSeasons.buttons.addSeason')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal isOpen={confirm.isOpen} onClose={confirm.onClose} onConfirm={remove}
        title={t('ratePlanSeasons.confirm.deleteTitle')} confirmLabel={t('ratePlanSeasons.confirm.deleteLabel')}
        message={deleteRuleCount > 0
          ? t('ratePlanSeasons.confirm.deleteWithRules', { name: deleteTarget?.name, count: deleteRuleCount })
          : t('ratePlanSeasons.confirm.deleteMessage', { name: deleteTarget?.name })} />
    </VStack>
  );
};

export default SeasonsPanel;
